import React, { useEffect, useState, useCallback, useRef } from 'react';
import { getBetHistory, getEvent, requestCashoutQuote, executeCashout } from '../api/api';
import { TicketOut, TicketLegOut, CashoutQuoteOut } from '../api/types';
import { useTranslation } from '../i18n';
import { useLiveScores } from '../hooks/useLiveScores';
import { isTicketWon, isTicketLost, isOpenForCashout } from '../utils/ticketHelpers';
import { resolveMarketName, resolveOutcomeName } from '../utils/marketTranslations';

interface DesktopMyBetsPageProps {
  onLoginClick: () => void;
}

// ── Status helpers ────────────────────────────────────────────────────────────
type TabId = 'all' | 'open' | 'settled' | 'cashed_out';

// ── Payout calculation ────────────────────────────────────────────────────────

/** Readable German label for a ticket's status */
const statusLabel = (t: TicketOut): string => {
  switch (t.status) {
    case 'open':
    case 'accepted':
      return 'Offen';
    case 'settled':
    case 'early_settled':
      return isTicketWon(t) ? 'Gewonnen' : isTicketLost(t) ? 'Verloren' : 'Abgerechnet';
    case 'cashed_out':
      return 'Cashout';
    case 'rejected':
      return 'Abgelehnt';
    case 'cancelled':
      return 'Storniert';
    default:
      return t.status;
  }
};

/** Readable German label for a leg's result_status */
export const legStatusLabel = (leg: TicketLegOut): string => {
  switch (leg.result_status) {
    case 'not_resulted':
    case 'pending':
    case 'open':
      return 'Ausstehend';
    case 'won':
    case 'win':
      return 'Gewonnen';
    case 'lost':
    case 'lose':
      return 'Verloren';
    case 'void':
    case 'cancelled':
      return 'Ungültig';
    case 'half_won':
      return 'Halb gewonnen';
    case 'half_lost':
      return 'Halb verloren';
    default:
      return leg.result_status || 'Ausstehend';
  }
};

/** CSS classes for a ticket's overall status badge */
const statusColor = (t: TicketOut): string => {
  switch (t.status) {
    case 'open':
    case 'accepted':
      return 'text-blue-700';
    case 'settled':
    case 'early_settled':
      return isTicketWon(t) ? 'text-green-700' : isTicketLost(t) ? 'text-red-700' : 'text-gray-700 dark:text-gray-300';
    case 'cashed_out':
      return 'text-amber-700';
    case 'rejected':
    case 'cancelled':
      return 'text-gray-600 dark:text-gray-400';
    default:
      return 'text-gray-700 dark:text-gray-300';
  }
};

/** CSS classes for a leg's result status icon */
export const legStatusIcon = (leg: TicketLegOut): React.ReactNode => {
  const rs = leg.result_status;
  if (rs === 'won' || rs === 'win') {
    return <span className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0"><svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg></span>;
  }
  if (rs === 'lost' || rs === 'lose') {
    return <span className="w-5 h-5 rounded-full bg-red-500 flex items-center justify-center flex-shrink-0"><svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg></span>;
  }
  if (rs === 'void' || rs === 'cancelled') {
    return <span className="w-5 h-5 rounded-full bg-gray-300 flex items-center justify-center flex-shrink-0"><svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M18 12H6" /></svg></span>;
  }
  // pending / not_resulted
  return <span className="w-5 h-5 rounded-full bg-blue-400 flex items-center justify-center flex-shrink-0"><span className="w-2 h-2 rounded-full bg-white dark:bg-[#1a1a1a] dark:text-white animate-pulse" /></span>;
};

// ── Market & Outcome resolution ───────────────────────────────────────────────
export const resolveMarketAndOutcome = (leg: TicketLegOut, eventName?: string, locale: string = 'de') => {
  const specMap: Record<string, string> = {};
  if (leg.specifiers_norm) {
    leg.specifiers_norm.split('|').forEach((s: string) => {
      const [k, v] = s.split('=');
      if (k && v) specMap[k] = v;
    });
  }

  // Ticket legs carry no market_name from the API, so a language-pack miss used
  // to surface the raw market_id (e.g. "38: Henderson, Jordan"). Fall back to
  // the English pack before ever showing the bare id.
  let marketName = resolveMarketName(leg.market_id, locale, specMap, undefined, eventName)
    || (locale !== 'en' ? resolveMarketName(leg.market_id, 'en', specMap, undefined, eventName) : null)
    || leg.market_name
    || leg.market_id;
  let outcomeName = resolveOutcomeName(leg.market_id, leg.outcome_id, locale, specMap, eventName);

  if (outcomeName === leg.outcome_id && leg.outcome_name) {
    outcomeName = leg.outcome_name;
  }

  return { marketName, outcomeName };
};

// ── Formatting helpers ────────────────────────────────────────────────────────
const fmtOdds = (v: string): string => {
  const n = parseFloat(v);
  return isNaN(n) ? v : n.toFixed(2).replace('.', ',');
};

const fmtAmount = (v: string | undefined, currency?: string): string => {
  const n = parseFloat(v ?? '0');
  const formatted = isNaN(n) ? '0,00' : n.toFixed(2).replace('.', ',');
  return `${formatted} ${currency === 'USD' ? '$' : '€'}`;
};

const fmtDate = (iso: string): string => {
  return new Date(iso).toLocaleString('de-DE', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const fmtDateEvent = (iso: string): string => {
  const d = new Date(iso);
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');
  return `${day}/${month}, ${hours}:${minutes}`;
};

// ── Payout calculation ────────────────────────────────────────────────────────
const getDisplayPayout = (t: TicketOut): { amount: string; color: string } => {
  const settled = t.status === 'settled' || t.status === 'early_settled';
  const cashedOut = t.status === 'cashed_out';

  if (cashedOut) {
    const payout = parseFloat(t.financial_payout_posted || '0');
    return { amount: fmtAmount(payout.toString(), t.currency), color: 'text-amber-600' };
  }
  if (settled && isTicketWon(t)) {
    const payout = parseFloat(t.financial_payout_posted || t.potential_payout);
    return { amount: fmtAmount(payout.toString(), t.currency), color: 'text-green-600' };
  }
  if (settled && isTicketLost(t)) {
    return { amount: fmtAmount('0', t.currency), color: 'text-red-500' };
  }
  // open / other
  const amtStr = fmtAmount(t.potential_payout, t.currency);
  const exactMark = t.potential_payout_is_exact === false ? '~' : '';
  return { amount: exactMark + amtStr, color: 'text-gray-900 dark:text-white' };
};

const renderLegScores = (leg: TicketLegOut, translate: (k: string) => string) => {
  if (!leg.current_team_scores && !leg.placement_team_scores) return null;
  return (
    <div className="grid grid-cols-[auto_1fr] gap-x-1.5 gap-y-1 mt-1.5 p-1.5 bg-gray-50 dark:bg-[#252525]/80 rounded border border-gray-100 dark:border-[#333]/60 w-full items-center text-[10px] leading-none">
      {leg.placement_team_scores && (
        <>
          <span className="text-gray-500 dark:text-gray-400 font-medium tracking-wide whitespace-nowrap">{translate('slip.placementScore')}</span>
          <div className="flex items-center gap-1 flex-wrap">
            <span className="font-bold text-gray-700 dark:text-gray-300">{leg.placement_team_scores.home_score} - {leg.placement_team_scores.away_score}</span>
            {leg.placement_period_scores && leg.placement_period_scores.length > 0 && (
              <span className="text-gray-400 font-medium text-[9px]">
                {leg.placement_period_scores.map(p => `(${p.home_score}-${p.away_score})`).join(',')}
              </span>
            )}
          </div>
        </>
      )}
      {leg.current_team_scores && (
        <>
          <span className="text-gray-500 dark:text-gray-400 font-medium tracking-wide whitespace-nowrap">{translate('slip.currentScore')}</span>
          <div className="flex items-center gap-1 flex-wrap">
            <span className="font-bold text-gray-700 dark:text-gray-300">{leg.current_team_scores.home_score} - {leg.current_team_scores.away_score}</span>
            {leg.current_period_scores && leg.current_period_scores.length > 0 && (
              <span className="text-gray-400 font-medium text-[9px]">
                {leg.current_period_scores.map(p => `(${p.home_score}-${p.away_score})`).join(',')}
              </span>
            )}
          </div>
        </>
      )}
    </div>
  );
};

const renderLegsList = (t: TicketOut, eventDetails: any, eventNames: any, liveScores: any, locale: string, isMobile: boolean) => {
  const legsByEvent: Record<string, TicketLegOut[]> = {};
  t.legs?.forEach(leg => {
    if (!legsByEvent[leg.event_id]) legsByEvent[leg.event_id] = [];
    legsByEvent[leg.event_id].push(leg);
  });

  return (
    <div className={`flex flex-col ${isMobile ? 'divide-y divide-gray-100' : 'gap-0 px-4 py-2'}`}>
      {Object.values(legsByEvent).map((group, groupIdx) => {
        const firstLeg = group[0];
        
        const fetchedData = eventDetails[firstLeg.event_id];
        const fetchedEvent = fetchedData?.event;
        const fetchedStatus = fetchedData?.status;
        
        const ticketEvent = t.events?.find(e => e.event_id === firstLeg.event_id);
        const event = {
          ...ticketEvent?.event,
          ...firstLeg.event,
          ...(fetchedEvent ? {
            home: fetchedEvent.home,
            away: fetchedEvent.away,
            category_name: fetchedEvent.category_name,
            tournament_name: fetchedEvent.tournament_name
          } : {}),
          current_state: ticketEvent?.current_state || (firstLeg.event as any)?.current_state || (fetchedStatus ? {
            team_scores: { home_score: fetchedStatus.home_score, away_score: fetchedStatus.away_score },
            period_scores: fetchedStatus.period_scores
          } : undefined),
          placement_state: ticketEvent?.placement_state || (firstLeg.event as any)?.placement_state
        } as any;

        const eventNameFallback = eventNames[firstLeg.event_id];
        const eventName = (event?.home?.name && event?.away?.name) ? `${event.home.name} - ${event.away.name}` : eventNameFallback;
        const displayName = eventName || firstLeg.event_name || `Event ${firstLeg.event_id.split(':').pop()}`;
        const currentTeamScores = event?.current_state?.team_scores;
        const currentPeriodScores = event?.current_state?.period_scores;
        const placementTeamScores = event?.placement_state?.team_scores;
        const eventTime = event?.scheduled_at || event?.start_time || t.created_at;

        if (group.length === 1) {
          const leg = firstLeg;
          const fetchedMarket = fetchedData?.markets?.find((m: any) => m.market_id === leg.market_id);
          const { marketName: resolvedMarketName, outcomeName: resolvedOutcomeName } = resolveMarketAndOutcome(leg, displayName, locale);
          const marketName = resolvedMarketName || fetchedMarket?.name || leg.market_name || leg.market_id;
          const fallbackOutcomeName = fetchedMarket?.outcomes?.find((o: any) => o.outcome_id === leg.outcome_id)?.name || leg.outcome_name || leg.outcome_id;
          const outcomeName = resolvedOutcomeName !== leg.outcome_id ? resolvedOutcomeName : fallbackOutcomeName;

          return (
            <div key={leg.id || groupIdx} className={`flex items-start gap-3 ${isMobile ? 'py-3.5' : 'py-2.5 border-b last:border-0 border-dashed border-gray-100 dark:border-[#333]'}`}>
              <div className={isMobile ? 'mt-0.5' : ''}>{legStatusIcon(leg)}</div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <div className="text-[13px] text-gray-800 dark:text-gray-200 leading-tight">
                    {marketName}: <span className="font-bold">{outcomeName}</span>
                  </div>
                  <span className={`text-[13px] font-bold flex-shrink-0 ${leg.result_status === 'won' || leg.result_status === 'win' ? 'text-green-500' : leg.result_status === 'lost' || leg.result_status === 'lose' ? 'text-red-500' : 'text-gray-900 dark:text-white'}`}>{fmtOdds(leg.accepted_odds)}</span>
                </div>
                <div className="text-[13px] text-gray-500 dark:text-gray-400 mt-1 truncate">
                  {displayName}
                </div>
                {event?.category_name && event?.tournament_name && (
                  <div className="text-[13px] text-gray-500 dark:text-gray-400 truncate">
                    {event.category_name} &gt; {event.tournament_name}
                  </div>
                )}
                <div className="text-[13px] text-gray-500 dark:text-gray-400 mt-0.5 flex flex-wrap gap-x-2 gap-y-0.5 items-center">
                  {eventTime && <span>{fmtDateEvent(eventTime as string)}</span>}
                  {currentTeamScores && (
                    <>
                      <span className="text-gray-500 dark:text-gray-400">|</span>
                      <span>
                        {currentTeamScores.home_score} - {currentTeamScores.away_score}
                        {currentPeriodScores && currentPeriodScores.length > 0 && ` ( ${currentPeriodScores.map((p: any) => `${p.home_score} - ${p.away_score}`).join(', ')} )`}
                      </span>
                    </>
                  )}
                  {placementTeamScores && (
                    <>
                      <span className="text-gray-500 dark:text-gray-400">|</span>
                      <span>bei Wettabgabe: {placementTeamScores.home_score} - {placementTeamScores.away_score}</span>
                    </>
                  )}
                </div>
                {leg.result_status && !['not_resulted', 'pending', 'open', 'won', 'win', 'lost', 'lose'].includes(leg.result_status) && (
                  <div className="text-[11px] text-gray-400 mt-1 bg-gray-50 dark:bg-[#252525] px-1.5 py-0.5 rounded inline-block font-medium">
                    {legStatusLabel(leg)}
                  </div>
                )}
              </div>
            </div>
          );
        }

        // MatchKombi grouped rendering
        return (
          <div key={`mk-${firstLeg.event_id}`} className={`flex items-start gap-3 ${isMobile ? 'py-3.5' : 'py-2.5 border-b last:border-0 border-dashed border-gray-100 dark:border-[#333]'}`}>
            <div className="flex-1 min-w-0 bg-white dark:bg-[#1a1a1a] dark:text-white rounded-lg border border-gray-200 dark:border-[#333] border-l-[3px] border-l-[#c8102e] shadow-sm overflow-hidden">
              <div className="px-3 py-3 bg-gray-50 dark:bg-[#252525]/50 border-b border-gray-100 dark:border-[#333]">
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-1.5">
                    <span className="text-[10px] font-black text-white bg-[#c8102e] px-1.5 py-0.5 rounded leading-none">MK+</span>
                    <span className="text-sm font-bold text-gray-900 dark:text-white">{group.length}er MatchKombi</span>
                  </div>
                  {(() => {
                    let mkOdds = t.event_groups?.find((g: any) => g.event_id === firstLeg.event_id && g.operator === 'all')?.accepted_odds;
                    if (!mkOdds && t.legs?.length === group.length) {
                      mkOdds = t.accepted_total_odds;
                    }
                    return mkOdds ? <span className="text-sm font-black text-gray-900 dark:text-white pr-1">{fmtOdds(mkOdds)}</span> : null;
                  })()}
                </div>
                <div className="text-[12px] text-gray-500 dark:text-gray-400 font-medium truncate">
                  {displayName}
                </div>
                {event?.category_name && event?.tournament_name && (
                  <div className="text-[11px] text-gray-400 truncate mt-0.5">
                    {event.category_name} &gt; {event.tournament_name}
                  </div>
                )}
              </div>
              <div className="flex flex-col gap-2 p-3 bg-white dark:bg-[#1a1a1a] dark:text-white pl-4 border-l-2 border-gray-100 dark:border-[#333] ml-3 my-2">
                {group.map((leg, legIdx) => {
                  const fetchedMarket = fetchedData?.markets?.find((m: any) => m.market_id === leg.market_id);
                  const { marketName: resolvedMarketName, outcomeName: resolvedOutcomeName } = resolveMarketAndOutcome(leg, displayName, locale);
                  const marketName = resolvedMarketName || fetchedMarket?.name || leg.market_name || leg.market_id;
                  const fallbackOutcomeName = fetchedMarket?.outcomes?.find((o: any) => o.outcome_id === leg.outcome_id)?.name || leg.outcome_name || leg.outcome_id;
                  const outcomeName = resolvedOutcomeName !== leg.outcome_id ? resolvedOutcomeName : fallbackOutcomeName;

                  return (
                    <div key={leg.id || legIdx} className="flex items-start justify-between gap-3">
                      <div className="text-[12.5px] leading-snug text-gray-700 dark:text-gray-300">
                        <span className="text-gray-500 dark:text-gray-400">{marketName}:</span> <span className="font-bold text-gray-900 dark:text-white">{outcomeName}</span>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <div className="scale-[0.8] origin-right">
                          {legStatusIcon(leg)}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// Component
// ══════════════════════════════════════════════════════════════════════════════
const DesktopMyBetsPage: React.FC<DesktopMyBetsPageProps> = ({ onLoginClick }) => {
  const { t: translate, locale } = useTranslation();
  const [tickets, setTickets] = useState<TicketOut[]>([]);
  const [eventNames, setEventNames] = useState<Record<string, string>>({});
  const [eventDetails, setEventDetails] = useState<Record<string, any>>({});
  const fetchedEventIds = useRef(new Set<string>());
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabId>('all');
  const [expandedTicket, setExpandedTicket] = useState<string | null>(null);
  const [selectedTicketForModal, setSelectedTicketForModal] = useState<TicketOut | null>(null);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 1024);
  const isLogged = !!localStorage.getItem('access_token');
  const threeDaysAgo = new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
  const tomorrow = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().split('T')[0];
  const [dateFrom, setDateFrom] = useState(threeDaysAgo);
  const [dateTo, setDateTo] = useState(tomorrow);
  const liveScores = useLiveScores(tickets);

  const [cashoutQuotes, setCashoutQuotes] = useState<Record<string, CashoutQuoteOut>>({});
  const [cashoutLoading, setCashoutLoading] = useState<Record<string, boolean>>({});
  const [cashoutErrors, setCashoutErrors] = useState<Record<string, string>>({});
  const [cashoutConfirming, setCashoutConfirming] = useState<Record<string, boolean>>({});

  const fetchQuotes = useCallback(async () => {
    if (!isLogged) return;
    const openTickets = tickets.filter(t => isOpenForCashout(t) && t.ticket_type !== 'system' && t.acca_mode !== 'multi_way');
    if (openTickets.length === 0) return;

    openTickets.forEach(t => {
      requestCashoutQuote(t.id).then(quote => {
        setCashoutQuotes(prev => ({ ...prev, [t.id]: quote }));
      }).catch(() => {
        setCashoutQuotes(prev => {
          if (!prev[t.id]) return prev;
          const next = { ...prev };
          delete next[t.id];
          return next;
        });
      });
    });
  }, [tickets, isLogged]);

  useEffect(() => {
    fetchQuotes();
    const intervalId = setInterval(fetchQuotes, 5000);
    return () => clearInterval(intervalId);
  }, [fetchQuotes]);


  const handleConfirmCashout = async (ticketId: string, quoteId: string) => {
    setCashoutLoading(prev => ({ ...prev, [ticketId]: true }));
    setCashoutErrors(prev => ({ ...prev, [ticketId]: '' }));
    try {
      const updatedTicket = await executeCashout(quoteId);

      setCashoutQuotes(prev => {
        const next = { ...prev };
        delete next[ticketId];
        return next;
      });
      setCashoutConfirming(prev => ({ ...prev, [ticketId]: false }));

      // Update the ticket state with the new cashed out ticket
      setTickets(prev => prev.map(t => t.id === ticketId ? updatedTicket : t));

      window.dispatchEvent(new Event('balance-changed'));
    } catch (err: any) {
      setCashoutErrors(prev => ({ ...prev, [ticketId]: err.message || translate('slip.cashoutFailed') }));
    } finally {
      setCashoutLoading(prev => ({ ...prev, [ticketId]: false }));
    }
  };

  const handleCancelCashout = (ticketId: string) => {
    setCashoutConfirming(prev => ({ ...prev, [ticketId]: false }));
    setCashoutErrors(prev => ({ ...prev, [ticketId]: '' }));
  };

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  // Load ticket history
  useEffect(() => {
    if (isLogged) {
      setLoading(true);
      const fetchTickets = () => {
        getBetHistory(200, dateFrom, dateTo)
          .then((data) => setTickets(data))
          .catch(console.error)
          .finally(() => setLoading(false));
      };

      fetchTickets();
    } else {
      setLoading(false);
      setTickets([]);
    }
  }, [isLogged, dateFrom, dateTo]);



  // Parse event names from history API directly
  useEffect(() => {
    if (tickets.length === 0) return;

    const namesFromApi: Record<string, string> = {};

    tickets.forEach(t => t.legs?.forEach(l => {
      if (l.event_name) {
        namesFromApi[l.event_id] = l.event_name;
      }
    }));

    if (Object.keys(namesFromApi).length > 0) {
      setEventNames(prev => ({ ...prev, ...namesFromApi }));
    }
  }, [tickets]);

  // Fetch missing event details (category, tournament, full names, markets)
  useEffect(() => {
    if (tickets.length === 0) return;

    const missingEventIds = new Set<string>();

    tickets.forEach(t => t.legs?.forEach(l => {
      const ticketEvent = t.events?.find(e => e.event_id === l.event_id);
      const hasName = ticketEvent?.event?.home?.name || l.event?.home?.name;
      if (!hasName && !fetchedEventIds.current.has(l.event_id)) {
        missingEventIds.add(l.event_id);
        fetchedEventIds.current.add(l.event_id);
      }
    }));

    missingEventIds.forEach(id => {
      getEvent(id).then(res => {
        setEventDetails(prev => ({ ...prev, [id]: res }));
      }).catch(console.error);
    });
  }, [tickets]);


  // ── Not logged in ────────────────────────────────────────────────────────
  if (!isLogged) {
    return (
      <div className="flex flex-col items-center justify-center p-16 bg-white dark:bg-[#1a1a1a] dark:text-white rounded-lg shadow-sm border border-gray-200 dark:border-[#333]">
        <svg className="w-16 h-16 text-gray-300 mb-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Meine Wetten</h2>
        <p className="text-gray-500 dark:text-gray-400 mb-6">Bitte melde dich an, um deine Wetten zu sehen.</p>
        <button onClick={onLoginClick} className="bg-tipico-red text-white px-8 py-3 rounded text-sm font-bold shadow hover:bg-tipico-red-dark transition">Anmelden</button>
      </div>
    );
  }

  // ── Filter ───────────────────────────────────────────────────────────────
  const filteredTickets = tickets.filter(t => {
    if (activeTab === 'all') return true;
    if (activeTab === 'open') return t.status === 'open' || t.status === 'accepted';
    if (activeTab === 'settled') return t.status === 'settled' || t.status === 'early_settled';
    if (activeTab === 'cashed_out') return t.status === 'cashed_out';
    return true;
  });

  // ── Stats ────────────────────────────────────────────────────────────────
  const openCount = tickets.filter(t => t.status === 'open' || t.status === 'accepted').length;
  const settledCount = tickets.filter(t => t.status === 'settled' || t.status === 'early_settled').length;
  const cashoutCount = tickets.filter(t => t.status === 'cashed_out').length;

  const tabs: { id: TabId; label: string; count: number }[] = [
    { id: 'all', label: 'Alle', count: tickets.length },
    { id: 'open', label: 'Offen', count: openCount },
    { id: 'settled', label: 'Abgerechnet', count: settledCount },
    { id: 'cashed_out', label: 'Cashout', count: cashoutCount },
  ];

  return (
    <div className=" mx-auto w-full pb-10">
      <div className="bg-white dark:bg-[#1a1a1a] dark:text-white rounded-lg shadow-sm border border-gray-200 dark:border-[#333] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-4 py-4 sm:px-6 sm:py-5 border-b border-gray-200 dark:border-[#333] bg-gray-50 dark:bg-[#252525] flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <h1 className="text-xl font-black text-gray-900 dark:text-white uppercase tracking-tight">Meine Wetten</h1>
          <div className="flex items-center justify-between sm:justify-end gap-3 w-full sm:w-auto">
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="bg-white dark:bg-[#1a1a1a] dark:text-white border border-gray-300 text-gray-700 dark:text-gray-300 text-xs sm:text-sm rounded focus:ring-tipico-red focus:border-tipico-red block px-2.5 py-1.5 font-semibold shadow-sm"
              />
              <span className="text-gray-400 font-bold">-</span>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="bg-white dark:bg-[#1a1a1a] dark:text-white border border-gray-300 text-gray-700 dark:text-gray-300 text-xs sm:text-sm rounded focus:ring-tipico-red focus:border-tipico-red block px-2.5 py-1.5 font-semibold shadow-sm"
              />
            </div>
            <button
              onClick={() => {
                setLoading(true);
                getBetHistory(200, dateFrom, dateTo).then(setTickets).catch(console.error).finally(() => setLoading(false));
              }}
              className="text-[12px] text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:text-white font-bold flex items-center gap-1 transition"
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
              <span className="hidden sm:inline">Aktualisieren</span>
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 dark:border-[#333] px-4 sm:px-6 pt-3 gap-1 bg-white dark:bg-[#1a1a1a] dark:text-white shrink-0 overflow-x-auto no-scrollbar">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`pb-3 px-4 text-[13px] font-bold transition-all border-b-[3px] whitespace-nowrap flex items-center gap-2 ${activeTab === tab.id
                ? 'border-tipico-red text-gray-900 dark:text-white'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:text-gray-200 hover:border-gray-300'
                }`}
            >
              {tab.label}
              <span className={`text-[11px] px-1.5 py-0.5 rounded-full font-bold ${activeTab === tab.id ? 'bg-tipico-red text-white' : 'bg-gray-100 text-gray-500 dark:text-gray-400'}`}>
                {tab.count}
              </span>
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="p-3 sm:p-5 flex-1 flex flex-col min-h-[400px]">
          {loading ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-4 border-tipico-red border-t-transparent" />
            </div>
          ) : filteredTickets.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center">
              <svg className="w-14 h-14 text-gray-300 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p className="text-gray-500 dark:text-gray-400 font-medium text-center text-sm">Keine Wetten in diesem Bereich.</p>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {filteredTickets.map(t => {
                const isExpanded = expandedTicket === t.id;
                const payout = getDisplayPayout(t);
                let ticketTypeLabel = t.ticket_type === 'acca' ? translate('slip.combiBet') : translate('slip.singleBet');
                if (t.ticket_type === 'system' && t.system) {
                  const combs = t.system.combination_sizes?.join(', ');
                  const total = t.system.selection_count;
                  ticketTypeLabel = `Sistem (${combs}/${total})`;
                }
                const legsCount = t.legs?.length || 0;

                return (
                  <div
                    key={t.id}
                    className={`border rounded-lg overflow-hidden bg-white dark:bg-[#1a1a1a] dark:text-white transition-shadow ${isExpanded ? 'shadow-md border-gray-300' : 'shadow-sm border-gray-200 dark:border-[#333] hover:shadow-md'}`}
                  >
                    {/* Ticket header - clickable */}
                    <button
                      onClick={() => {
                        if (isMobile) {
                          setSelectedTicketForModal(t);
                        } else {
                          setExpandedTicket(isExpanded ? null : t.id);
                        }
                      }}
                      className="w-full text-left"
                    >
                      <div className="flex flex-col md:flex-row md:items-center bg-white dark:bg-[#1a1a1a] dark:text-white px-4 py-4 border-b border-gray-100 dark:border-[#333] gap-4 md:gap-0">
                        {/* Left Section (Status, Date, Type) */}
                        <div className="flex items-center flex-1 min-w-0">
                          <div className="w-[90px] flex-shrink-0">
                            <span className={`text-[14px] font-medium uppercase whitespace-nowrap ${statusColor(t)}`}>
                              {statusLabel(t)}
                            </span>
                          </div>
                          <div className="w-[85px] flex-shrink-0">
                            {t.created_at && (
                              <span className="text-[14px] text-gray-700 dark:text-gray-300 font-medium whitespace-nowrap">
                                {`${new Date(t.created_at).getDate()}.${new Date(t.created_at).getMonth() + 1} ${new Date(t.created_at).getHours().toString().padStart(2, '0')}:${new Date(t.created_at).getMinutes().toString().padStart(2, '0')}`}
                              </span>
                            )}
                          </div>
                          <div className="flex-1 min-w-0 pr-4">
                            <span className="text-[14px] text-gray-800 dark:text-gray-200 whitespace-nowrap truncate block">
                              {legsCount > 1 ? `${legsCount}x ` : ''}{ticketTypeLabel}
                            </span>
                          </div>
                        </div>

                        {/* Right Section (Financials & Actions) */}
                        <div className="flex items-center justify-between border-t md:border-t-0 border-gray-200 dark:border-[#333] pt-3 md:pt-0 w-full md:w-auto">
                          {!cashoutConfirming[t.id] && (
                            <>
                              <div className="w-[80px] flex flex-col text-left flex-shrink-0">
                                <span className="text-[13px] text-[#9d9d9d] font-semibold mb-0.5">Stake</span>
                                <span className="text-[13px] text-gray-900 dark:text-white">{fmtAmount(t.total_stake, t.currency)}</span>
                              </div>
                              <div className="w-[110px] flex flex-col text-left flex-shrink-0">
                                <span className="text-[13px] text-[#9d9d9d] font-semibold mb-0.5">{translate('slip.maxWin')}</span>
                                <span className="text-[13px] text-gray-900 dark:text-white">{fmtAmount(t.potential_payout, t.currency)}</span>
                              </div>
                            </>
                          )}

                          <div className={`${cashoutConfirming[t.id] ? 'w-full md:w-[330px]' : 'w-[140px]'} flex flex-col items-start flex-shrink-0 justify-center min-h-[38px] transition-all`}>
                            {isOpenForCashout(t) ? (() => {
                              const loading = cashoutLoading[t.id];
                              const quote = cashoutQuotes[t.id];
                              const err = cashoutErrors[t.id];
                              const confirming = cashoutConfirming[t.id];

                              if (quote && !confirming) {
                                return (
                                  <div className="flex flex-col items-start w-full">
                                    <button
                                      disabled={loading}
                                      onClick={(e) => { e.stopPropagation(); setCashoutConfirming(prev => ({ ...prev, [t.id]: true })); }}
                                      className="bg-[#00a650] hover:bg-[#009045] text-white font-bold text-xs px-2.5 py-1.5 rounded transition flex items-center gap-1"
                                    >
                                      <span>Cashout</span>
                                      <span>{parseFloat(quote.amount).toFixed(2).replace('.', ',')} €</span>
                                    </button>
                                    {err && <span className="text-[10px] text-red-500 font-semibold block mt-0.5 leading-tight">{err}</span>}
                                  </div>
                                );
                              }

                              if (quote && confirming) {
                                return (
                                  <div className="flex flex-col gap-1 items-end w-full pr-1">
                                    <div className="flex gap-2 w-full">
                                      <button disabled={loading} onClick={(e) => { e.stopPropagation(); handleConfirmCashout(t.id, quote.id); }} className="bg-green-500 hover:bg-green-600 text-white font-bold text-xs px-3 py-2 rounded flex-1 transition">Bestätigen</button>
                                      <button disabled={loading} onClick={(e) => { e.stopPropagation(); handleCancelCashout(t.id); }} className="bg-gray-300 hover:bg-gray-400 dark:bg-[#333] dark:hover:bg-[#444] text-gray-800 dark:text-gray-200 font-bold text-xs px-3 py-2 rounded flex-1 transition">Abbrechen</button>
                                    </div>
                                    {err && <span className="text-[11px] text-red-500 font-semibold block leading-tight text-right w-full">{err}</span>}
                                  </div>
                                );
                              }
                              return null;
                            })() : (t.status === 'settled' || t.status === 'early_settled' || t.status === 'cashed_out') ? (
                              <div className="flex flex-col text-left min-w-0">
                                <span className="text-[13px] text-[#9d9d9d] font-semibold mb-0.5">
                                  {t.status === 'cashed_out' ? 'Cashout' : 'Auszahlung'}
                                </span>
                                <span className={`text-[13px] font-semibold ${payout.color}`}>{payout.amount}</span>
                              </div>
                            ) : null}
                          </div>

                          <div className="w-[24px] flex justify-end flex-shrink-0">
                            <svg className={`w-5 h-5 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </div>
                        </div>
                      </div>
                    </button>



                    {/* Expanded legs detail */}
                    {isExpanded && (
                      <div className="bg-white dark:bg-[#1a1a1a] dark:text-white">
                        {/* Date row */}
                        <div className="px-4 py-2 border-b border-gray-50 flex items-center justify-between">
                          <span className="text-[11px] text-gray-400 font-medium">
                            Erstellt: {fmtDate(t.created_at)}
                          </span>
                          {t.settled_at && (
                            <span className="text-[11px] text-gray-400 font-medium">
                              Abgerechnet: {fmtDate(t.settled_at)}
                            </span>
                          )}
                        </div>

                        {/* Stake breakdown */}
                        {(parseFloat(t.stake_bonus) > 0) && (
                          <div className="px-4 py-1.5 flex items-center gap-4 text-[11px] text-gray-500 dark:text-gray-400 border-b border-gray-50">
                            <span>Cash-Einsatz: <strong className="text-gray-700 dark:text-gray-300">{fmtAmount(t.stake_cash, t.currency)}</strong></span>
                            <span>Bonus-Einsatz: <strong className="text-amber-600">{fmtAmount(t.stake_bonus, t.currency)}</strong></span>
                          </div>
                        )}

                        {/* Legs */}
                        {renderLegsList(t, eventDetails, eventNames, liveScores, locale, false)}

                        {/* Footer with betslip id */}
                        <div className="px-4 py-3 bg-gray-50 dark:bg-[#252525] border-t border-gray-100 dark:border-[#333]">
                          {isOpenForCashout(t) && t.ticket_type !== 'system' && t.acca_mode !== 'multi_way' ? (() => {
                            const loading = cashoutLoading[t.id];
                            const quote = cashoutQuotes[t.id];
                            const err = cashoutErrors[t.id];
                            const confirming = cashoutConfirming[t.id];
                            if (quote && !confirming) {
                                return (
                                    <button disabled={loading} onClick={(e) => { e.stopPropagation(); setCashoutConfirming(prev => ({ ...prev, [t.id]: true })); }} className="w-full mb-2 bg-[#00a650] hover:bg-[#009045] text-white font-bold text-xs px-2.5 py-1.5 rounded transition">
                                      Cashout {parseFloat(quote.amount).toFixed(2).replace('.', ',')} €
                                    </button>
                                );
                            }
                            return null;
                          })() : null}
                          <div className="text-[12px] text-gray-500 dark:text-gray-400">
                            Betslip ID: <strong className="text-gray-800 dark:text-gray-200">{t.ticket_reference || t.id}</strong>
                          </div>
                        </div>

                        {/* Rejection reason */}
                        {t.rejection_reason && (
                          <div className="px-4 py-2 bg-red-50 text-red-600 text-[12px] border-t border-red-100">
                            <strong>Grund:</strong> {t.rejection_reason}
                          </div>
                        )}
                      </div>
                    )}


                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Mobile Coupon Detail Modal */}
      {selectedTicketForModal && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[150] transition-opacity duration-300"
            onClick={() => setSelectedTicketForModal(null)}
          />

          <div className="fixed bottom-[76px] left-3 right-3 z-[160] flex flex-col max-h-[80vh] bg-white dark:bg-[#1a1a1a] dark:text-white rounded-2xl shadow-2xl overflow-hidden font-inter border border-gray-100 dark:border-[#333] animate-slide-up">
            {/* Header */}
            <div className="px-5 pt-4 pb-4 flex items-center justify-between border-b border-gray-100 dark:border-[#333]">
              <div className="flex flex-col">
                <span className="text-[13px] text-gray-500 dark:text-gray-400 font-bold uppercase tracking-wider">
                  {selectedTicketForModal.ticket_type === 'system' && selectedTicketForModal.system
                    ? `Sistem (${selectedTicketForModal.system.combination_sizes?.join(', ')}/${selectedTicketForModal.system.selection_count})`
                    : selectedTicketForModal.ticket_type === 'acca'
                      ? translate('slip.combiBet')
                      : translate('slip.singleBet')}
                </span>
                <span className="text-xs text-gray-500 dark:text-gray-400 font-mono mt-0.5">Betslip ID: <strong className="text-gray-800 dark:text-gray-200">{selectedTicketForModal.ticket_reference || selectedTicketForModal.id}</strong></span>
              </div>
              <button
                onClick={() => setSelectedTicketForModal(null)}
                className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition"
              >
                <svg className="w-5 h-5 text-gray-500 dark:text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Date and Status Bar */}
            <div className="px-5 py-3 bg-gray-50 dark:bg-[#252525] border-b border-gray-100 dark:border-[#333] flex items-center justify-between flex-shrink-0">
              <div className="text-[11px] text-gray-400 font-medium">
                Erstellt: {fmtDate(selectedTicketForModal.created_at)}
              </div>
              <span className={`text-[10px] uppercase font-bold ${statusColor(selectedTicketForModal)}`}>
                {statusLabel(selectedTicketForModal)}
              </span>
            </div>

            {/* Scrollable Legs List */}
            <div className="flex-1 overflow-y-auto px-5 py-2 divide-y divide-gray-100">
              {renderLegsList(selectedTicketForModal, eventDetails, eventNames, liveScores, locale, true)}
            </div>

            {/* Footer Summary */}
            <div className="bg-gray-50 dark:bg-[#252525] border-t border-gray-200 dark:border-[#333] px-5 pt-4 pb-6 flex flex-col gap-4 flex-shrink-0">
              {/* Stake breakdown */}
              {(parseFloat(selectedTicketForModal.stake_bonus) > 0) && (
                <div className="flex items-center gap-4 text-[11px] text-gray-500 dark:text-gray-400 justify-between">
                  <span>Cash-Einsatz: <strong className="text-gray-700 dark:text-gray-300">{fmtAmount(selectedTicketForModal.stake_cash, selectedTicketForModal.currency)}</strong></span>
                  <span>Bonus-Einsatz: <strong className="text-amber-600">{fmtAmount(selectedTicketForModal.stake_bonus, selectedTicketForModal.currency)}</strong></span>
                </div>
              )}

              {/* Betslip ID removed from here */}
              {isOpenForCashout(selectedTicketForModal) && selectedTicketForModal.ticket_type !== 'system' && selectedTicketForModal.acca_mode !== 'multi_way' ? (
                <div className="border-t border-gray-100 dark:border-[#333] pt-2 flex flex-col gap-2">
                  {(() => {
                    const loading = cashoutLoading[selectedTicketForModal.id];
                    const quote = cashoutQuotes[selectedTicketForModal.id];
                    const err = cashoutErrors[selectedTicketForModal.id];
                    const confirming = cashoutConfirming[selectedTicketForModal.id];

                    if (quote && !confirming) {
                      return (
                        <div className="flex flex-col gap-1">
                          <button
                            disabled={loading}
                            onClick={() => setCashoutConfirming(prev => ({ ...prev, [selectedTicketForModal.id]: true }))}
                            className="w-full bg-[#00a650] hover:bg-[#009045] text-white font-bold py-3.5 rounded-xl text-[15px] shadow-sm transition flex items-center justify-center gap-2"
                          >
                            <span>Cashout</span>
                            <span>{parseFloat(quote.amount).toFixed(2).replace('.', ',')} €</span>
                          </button>
                          {err && <p className="text-xs text-red-500 text-center font-semibold mt-1">{err}</p>}
                        </div>
                      );
                    }

                    if (quote && confirming) {
                      return (
                        <div className="flex flex-col gap-2 bg-white dark:bg-[#1a1a1a] dark:text-white p-3 rounded-xl border border-gray-100 dark:border-[#333]">
                          <div className="flex items-center justify-between text-xs font-semibold text-gray-700 dark:text-gray-300">
                            <span>{translate('slip.confirmCashout')}:</span>
                            <span className="text-green-600 font-extrabold text-sm">{parseFloat(quote.amount).toFixed(2).replace('.', ',')} €</span>
                          </div>
                          <div className="flex gap-2">
                            <button
                              disabled={loading}
                              onClick={() => handleConfirmCashout(selectedTicketForModal.id, quote.id)}
                              className="flex-1 bg-green-500 hover:bg-green-600 disabled:opacity-50 text-white font-bold text-sm py-3 rounded-lg transition"
                            >
                              {loading ? '...' : translate('misc.confirm')}
                            </button>
                            <button
                              disabled={loading}
                              onClick={() => handleCancelCashout(selectedTicketForModal.id)}
                              className="flex-1 bg-gray-200 hover:bg-gray-300 dark:bg-[#333] dark:hover:bg-[#444] disabled:opacity-50 text-gray-800 dark:text-gray-200 font-bold text-sm py-3 rounded-lg transition"
                            >
                              {translate('misc.cancel')}
                            </button>
                          </div>
                          {err && <p className="text-xs text-red-500 text-center font-semibold mt-1">{err}</p>}
                        </div>
                      );
                    }

                    return null;
              })()}
                </div>
              ) : null}

              {selectedTicketForModal.rejection_reason && (
                <div className="px-3 py-2 bg-red-50 text-red-600 text-[11px] rounded border border-red-100 leading-normal">
                  <strong>Grund:</strong> {selectedTicketForModal.rejection_reason}
                </div>
              )}

              <button
                onClick={() => setSelectedTicketForModal(null)}
                className="w-full bg-gray-900 text-white font-bold py-3.5 rounded-xl text-sm hover:bg-gray-800 transition active:scale-[0.98]"
              >
                Schließen
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default DesktopMyBetsPage;
